import { useEffect } from "react";
import RogersHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Rogers?", a: "We offer same-day processing options that can deliver your passport in as little as 24 hours. For Rogers and Benton County residents, our standard expedited service takes 7–10 business days, while our emergency service delivers in 24–72 hours." },
  { q: "Where is the nearest passport acceptance facility near Rogers?", a: "The closest acceptance facilities are at the Rogers Post Office and the Benton County Clerk's office. The XNA (Northwest Arkansas National Airport) area also has several convenient locations." },
  { q: "Can Walmart employees use your expedited passport service?", a: "Absolutely. We regularly assist Walmart corporate employees and vendor partners with passport renewals, new passports, and emergency processing for international business travel. We can handle group processing for teams." },
  { q: "Do I need an appointment for a passport in Rogers?", a: "Most acceptance facilities require appointments, especially during peak season in Northwest Arkansas. We help coordinate scheduling so you don't have to wait on hold." },
  { q: "Can I renew my passport by mail from Rogers?", a: "If your passport was issued within the last 15 years when you were 16 or older, you can renew by mail. Our expedited mail-in renewal service handles all the logistics." },
  { q: "What documents do I need for a child passport?", a: "For children under 16, you need proof of citizenship, parental identification, and both parents must appear or provide a notarized DS-3053 consent form. We guide you through every step." },
  { q: "How much does expedited passport service cost for Rogers residents?", a: "Our rates are typically 30–100% lower than FedEx and Staples. Government fees are $130 for a passport book (adult), plus our expedited service fee. We offer a best price guarantee." },
  { q: "What if I need a passport for a last-minute international trip from XNA?", a: "Contact us immediately. Our emergency 24-hour processing is specifically designed for these situations. Many travelers have made their flights with passports obtained through our emergency service." },
];

export default function RogersPage() {
  useEffect(() => {
    document.title = "Emergency Passport Rogers, AR — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/rogers/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Rogers, AR — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Walmart corporate travelers, Pinnacle Hills families, and all of Benton County.");
    setMeta("geo.placename", "Rogers, Arkansas");
    setMeta("geo.region", "US-AR");
    setMeta("geo.position", "36.3320;-94.1185");
    setMeta("ICBM", "36.3320, -94.1185");

    const faqs_data = faqs;
    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years." },
      { name: "New Passport", description: "First-time passport application assistance for Rogers and Benton County residents." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance." },
      { name: "Corporate Group Processing", description: "Bulk passport processing for Walmart and NWA corporate teams." },
    ];
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Arkansas", item: "https://fastpassportcenter.com/arkansas/" },
        { "@type": "ListItem", position: 3, name: "Rogers Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", url: pageUrl, telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Rogers", containedInPlace: { "@type": "State", name: "Arkansas" } },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({ "@type": "Service", serviceType: s.name, provider: { "@type": "LocalBusiness", name: "Fast Passport Center" }, areaServed: { "@type": "City", name: "Rogers" }, description: s.description })),
      { "@type": "FAQPage", mainEntity: faqs_data.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <RogersHeroSection />
      <nav className="sticky top-[70px] z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 overflow-x-auto py-3 no-scrollbar">
          {["Services", "Processing", "Local Area", "Timing", "How It Works", "FAQ"].map((label) => (
            <a key={label} href={`#${label.toLowerCase().replace(/ /g, "-")}`} className="text-sm font-medium text-[#1A365D] hover:text-[#F6C344] whitespace-nowrap px-3 py-1 rounded-md hover:bg-[#EEF2F8] transition-all">{label}</a>
          ))}
          <a href="https://fastpassportcenter.com/checkout/" className="ml-auto text-sm font-bold bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] px-4 py-1.5 rounded-md whitespace-nowrap transition-all">Get Started</a>
        </div>
      </nav>

      <section id="processing" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How Fast Can You Get a Passport in Rogers?</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Choose the speed that matches your travel timeline — from emergency processing to standard expedited service.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { speed: "Same-Day Emergency", time: "24–72 Hours", price: "From $599", best: true, desc: "For Walmart executives and travelers with immediate departure. Fastest option with dedicated courier." },
              { speed: "Expedited Rush", time: "3–5 Business Days", price: "From $349", best: false, desc: "Ideal for business travel within 2 weeks. Full tracking and document review included." },
              { speed: "Standard Expedited", time: "7–10 Business Days", price: "From $199", best: false, desc: "Great for planned international trips in 3–6 weeks. Best value with reliable processing." },
            ].map((opt) => (
              <div key={opt.speed} className={`relative rounded-lg border p-8 flex flex-col ${opt.best ? "border-[#F6C344] bg-[#FEFDF8]" : "border-gray-200 bg-white"}`}>
                {opt.best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F6C344] text-[#1A365D] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Most Popular</span>}
                <h3 className="font-bold text-[#1A365D] text-xl mb-2">{opt.speed}</h3>
                <p className="text-[#F6C344] font-bold text-2xl mb-1">{opt.time}</p>
                <p className="text-gray-500 text-sm mb-4">{opt.desc}</p>
                <div className="mt-auto">
                  <p className="text-[#1A365D] font-bold text-lg mb-4">{opt.price}</p>
                  <a href="https://fastpassportcenter.com/checkout/" className="block text-center bg-[#1A365D] hover:bg-[#0F2440] text-white font-semibold text-sm py-3 rounded-md transition-all whitespace-nowrap">Select This Speed</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Offer</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Rogers &amp; Benton County</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: "ri-flashlight-line", title: "Emergency Passport", desc: "24–72 hour processing for urgent departures from XNA or other regional airports." },
              { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for expired passports. Mail-in renewal available for eligible applicants." },
              { icon: "ri-passport-line", title: "New Passport", desc: "First-time application guidance with acceptance facility scheduling in Rogers or Bentonville." },
              { icon: "ri-alert-line", title: "Lost/Stolen Passport", desc: "DS-64 filing and replacement. Get a new passport within 24–72 hours for urgent travel." },
              { icon: "ri-parent-line", title: "Child Passport", desc: "Under-16 passport services with parental consent and document preparation." },
              { icon: "ri-team-line", title: "Corporate Group Processing", desc: "Bulk processing for Walmart, Daisy, and other NWA corporate teams traveling internationally." },
              { icon: "ri-stack-line", title: "Second Passport", desc: "For frequent corporate travelers needing two valid passports simultaneously." },
              { icon: "ri-tools-line", title: "Damaged Passport", desc: "Replacement for water-damaged, torn, or otherwise compromised passports." },
              { icon: "ri-edit-line", title: "Name Change", desc: "Marriage, divorce, or legal name change updates with court document guidance." },
            ].map((s) => (
              <div key={s.title} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${s.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-lg mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="local-area" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area Coverage</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Serving Rogers and the Greater NWA Region</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                <strong className="text-[#1A365D]">Rogers is one of the fastest-growing cities in the American South</strong>, driven by the explosive growth of the Northwest Arkansas corridor anchored by Walmart's global headquarters in nearby Bentonville. The city's Pinnacle Hills area has become a major commercial and residential hub, attracting corporate relocations from Fortune 500 suppliers and vendor companies. We specialize in expedited passport processing for corporate travelers who receive international assignment notices on short timelines.
              </p>
              <p>
                <strong className="text-[#1A365D]">Daisy Outdoor Products</strong>, headquartered in Rogers, sends engineers and executives to trade shows and supplier meetings in Germany, Japan, and China. <strong className="text-[#1A365D]">J.B. Hunt Transport Services</strong> has facilities in the region, with logistics professionals traveling internationally for supply chain meetings. We provide business-priority passport processing for NWA's thriving corporate community.
              </p>
              <p>
                We serve all of Benton County, including <strong className="text-[#1A365D]">Bentonville, Springdale, Lowell, Pea Ridge, Gravette, and Bella Vista</strong>. Rogers families planning European river cruises, Caribbean vacations, or mission trips to Central America rely on our expedited service for all family members. We can process multiple passports simultaneously, ideal for families with multiple children applying for the first time.
              </p>
              <p>
                <strong className="text-[#1A365D]">Northwest Arkansas National Airport (XNA)</strong> in Bentonville provides direct service to major hubs. Most international travelers connect through Dallas/Fort Worth, Chicago O'Hare, or Denver. We ensure you have your passport well in advance of your connection.
              </p>
            </div>
            <div className="space-y-4">
              <div className="bg-[#F7F8FA] rounded-lg p-6">
                <h4 className="font-bold text-[#1A365D] mb-4">Areas We Serve</h4>
                <div className="grid grid-cols-2 gap-3">
                  {["Rogers", "Bentonville", "Springdale", "Lowell", "Pea Ridge", "Gravette", "Bella Vista", "Centerton", "Highfill", "Siloam Springs", "Cave Springs", "Elm Springs"].map((area) => (
                    <div key={area} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-map-pin-line text-[#F6C344]"></i>{area}</div>
                  ))}
                </div>
              </div>
              <div className="bg-[#F7F8FA] rounded-lg p-6">
                <h4 className="font-bold text-[#1A365D] mb-4">Nearby Airports</h4>
                <div className="space-y-2">
                  {[
                    { name: "NW Arkansas National (XNA)", dist: "Local — Bentonville" },
                    { name: "Tulsa International (TUL)", dist: "90 miles northeast" },
                    { name: "Fort Smith Regional (FSM)", dist: "65 miles southeast" },
                  ].map((a) => (
                    <div key={a.name} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{a.name}</span>
                      <span className="text-gray-400">{a.dist}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="timing" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Best Times to Apply from Rogers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { season: "Spring (Mar–May)", tag: "Corporate Season", desc: "Walmart fiscal year travel and trade show season. Apply 8 weeks before travel. Family spring break demand peaks in March." },
              { season: "Summer (Jun–Aug)", tag: "High Demand", desc: "Peak family vacation season. Summer camps with international travel programs. Apply early for July/August trips." },
              { season: "Fall/Winter (Sep–Feb)", tag: "Best Processing", desc: "Fastest government processing times. Q4 corporate travel peaks in October–November. Holiday cruise demand in December." },
            ].map((t) => (
              <div key={t.season} className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-[#1A365D] text-lg">{t.season}</h3>
                  <span className="text-xs font-semibold bg-[#EEF2F8] text-[#1A365D] px-2 py-1 rounded-full whitespace-nowrap">{t.tag}</span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Rogers Travelers Are Going</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { dest: "China & Japan", icon: "ri-building-2-line", desc: "Walmart supplier visits and tech conference trips. Year-round corporate travel with peaks in Q1 and Q3." },
              { dest: "European River Cruises", icon: "ri-ship-line", desc: "Rhine and Danube cruises. Popular with Bentonville and Rogers families and retirees." },
              { dest: "Caribbean & Mexico", icon: "ri-sun-line", desc: "Family vacations from Dallas or Houston cruise ports. Spring break and summer peaks." },
              { dest: "UK & Germany", icon: "ri-plane-line", desc: "Corporate travel for NWA manufacturing and retail executives. Trade shows in Dusseldorf and London." },
            ].map((d) => (
              <div key={d.dest} className="bg-[#F7F8FA] rounded-lg p-6 text-center hover:bg-[#EEF2F8] transition-all">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white mx-auto mb-4">
                  <i className={`${d.icon} text-[#1A365D] text-2xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] mb-2">{d.dest}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1A365D] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Traveler Reviews</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">What Rogers &amp; NWA Residents Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Marcus R.", location: "Walmart Global Sourcing, Rogers", text: "I got a last-minute assignment to Shenzhen and my passport was expired. Fast Passport Center had a new one in my hands in 36 hours. Literally saved my career moment. Use them if you're Walmart." },
              { name: "Jennifer H.", location: "Pinnacle Hills, Rogers", text: "We planned a family European river cruise and needed passports for all four kids. They walked us through every step and all four passports arrived in one week. Brilliant service." },
              { name: "Tom B.", location: "Pea Ridge, AR", text: "My wife and I planned an anniversary trip to Italy. When we realized our passports had expired, I panicked. Fast Passport Center had them renewed in 6 days. Everything went perfectly." },
            ].map((t, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-lg p-6 border border-white/10">
                <div className="flex items-center gap-1 mb-4">{[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
                <p className="text-white/90 text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F6C344] flex items-center justify-center text-[#1A365D] font-bold text-sm">{t.name[0]}</div>
                  <div><p className="text-white font-semibold text-sm">{t.name}</p><p className="text-white/60 text-xs">{t.location}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Rogers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { name: "Rogers Post Office", address: "2020 W Oak St, Rogers, AR 72756", phone: "(479) 636-5940", type: "Post Office", note: "Appointment required for passport applications." },
              { name: "Benton County Clerk", address: "215 E Central Ave, Bentonville, AR 72712", phone: "(479) 271-1012", type: "County Clerk", note: "Serves Benton County including Rogers. Call ahead for availability." },
            ].map((f) => (
              <div key={f.name} className="bg-[#F7F8FA] rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-government-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">{f.type}</span></div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600"><i className="ri-map-pin-line text-[#F6C344]"></i>{f.address}</div>
                  <div className="flex items-center gap-2 text-gray-600"><i className="ri-phone-line text-[#F6C344]"></i>{f.phone}</div>
                  <div className="flex items-center gap-2 text-gray-500"><i className="ri-information-line text-[#F6C344]"></i>{f.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Other Options</h2>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-4 bg-[#1A365D] text-white text-sm font-semibold py-4 px-6">
              <div>Feature</div><div className="text-center">Fast Passport Center</div><div className="text-center">FedEx / Staples</div><div className="text-center">DIY at Post Office</div>
            </div>
            {[["Speed","24 hrs – 10 days","2–6 weeks","8–11 weeks"],["Price","30–100% lower","Expensive markup","Lowest base cost"],["Personal Support","Dedicated agent","Limited / None","None"],["Document Review","Expert review included","Extra fee","Self-check"],["Real-Time Tracking","Yes","Basic","No"],["State Dept. Courier","Registered","Reseller","N/A"],["Best Price Guarantee","Yes","No","N/A"],["A+ BBB Rating","Yes","Varies","N/A"],["Money-Back Guarantee","Yes","Varies","No"]].map((row, i) => (
              <div key={i} className={`grid grid-cols-4 py-4 px-6 text-sm ${i % 2 === 0 ? "bg-[#F7F8FA]" : "bg-white"}`}>
                <div className="text-[#1A365D] font-semibold">{row[0]}</div><div className="text-center text-green-700 font-medium">{row[1]}</div><div className="text-center text-gray-500">{row[2]}</div><div className="text-center text-gray-500">{row[3]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Don't Let These Errors Delay Your Trip</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Wrong Photo Size", desc: "Must be 2x2 inches with white background. We provide exact specifications to avoid rejection." },
              { title: "Expired Name Documents", desc: "Name changes require certified marriage certificate or court order. Photocopies not accepted." },
              { title: "Incomplete DS-11 Forms", desc: "Missing signatures are the #1 delay. We review every form before submission." },
              { title: "Wrong Payment Amount", desc: "Government fees change. We confirm current amounts so your payment is accurate." },
              { title: "Missing Parental Consent", desc: "Children under 16 need both parents or notarized DS-3053. We help with this requirement." },
              { title: "Waiting Too Long", desc: "Standard processing takes 8–11 weeks. Start your application the moment you book travel." },
            ].map((m) => (
              <div key={m.title} className="bg-[#F7F8FA] rounded-lg p-6 border-l-4 border-[#F6C344]">
                <h3 className="font-bold text-[#1A365D] mb-2">{m.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Simple Process</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works in 4 Steps</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Start Online", desc: "Choose your processing speed and complete our secure online application. Takes 5 minutes." },
              { step: "02", title: "Document Prep", desc: "We review your documents and schedule your acceptance facility visit near Rogers or Bentonville." },
              { step: "03", title: "Courier Handoff", desc: "Our registered State Department courier picks up your sealed envelope and delivers it directly." },
              { step: "04", title: "Passport Delivered", desc: "Your passport arrives at your Rogers address via secure overnight shipping." },
            ].map((s) => (
              <div key={s.step} className="bg-white rounded-lg p-6 text-center border border-gray-200">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4"><span className="text-[#1A365D] font-bold text-xl">{s.step}</span></div>
                <h3 className="font-bold text-[#1A365D] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="bg-[#F7F8FA] rounded-lg border border-gray-200 group">
                <summary className="flex items-center justify-between p-5 cursor-pointer text-[#1A365D] font-semibold text-base hover:text-[#F6C344] transition-colors">
                  {faq.q}<i className="ri-add-line text-[#F6C344] group-open:rotate-45 transition-transform"></i>
                </summary>
                <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Links for Rogers Travelers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "U.S. State Department", desc: "Official passport requirements and forms.", link: "https://travel.state.gov/content/travel/en/passports.html" },
              { title: "TSA PreCheck", desc: "Speed through security at XNA, DFW, and ORD airports.", link: "https://www.tsa.gov/precheck" },
              { title: "Global Entry", desc: "Expedited customs clearance — ideal for Walmart frequent flyers.", link: "https://www.cbp.gov/travel/trusted-traveler-programs/global-entry" },
              { title: "CDC Travel Health", desc: "Vaccination and health notices for international destinations.", link: "https://wwwnc.cdc.gov/travel" },
              { title: "Smart Traveler Enrollment", desc: "Register your trip with the U.S. Embassy.", link: "https://step.state.gov/" },
              { title: "XNA Airport", desc: "NW Arkansas National Airport — direct flights to major hubs.", link: "https://www.flyxna.com" },
            ].map((r) => (
              <a key={r.title} href={r.link} target="_blank" rel="noopener noreferrer nofollow" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
                <h3 className="font-bold text-[#1A365D] mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
                <p className="text-gray-500 text-sm mb-3">{r.desc}</p>
                <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-medium group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <SEOCrossLinks currentRoute="/cities/rogers" state="AR" />

      <section className="bg-[#1A365D] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Get Your Passport in Rogers?</h2>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Whether you are a Walmart executive with a last-minute China trip or a family planning a Caribbean cruise, we get your passport done fast.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
              Start My Passport <i className="ri-arrow-right-line"></i>
            </a>
            <a href="tel:8772530084" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              <i className="ri-phone-line"></i>877-253-0084
            </a>
          </div>
          <p className="text-white/50 text-sm mt-6">Free consultation. No obligation. A+ BBB rated.</p>
        </div>
      </section>
    </div>
  );
}